import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Phone, Instagram, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!fullName || !email || !message) {
      toast.error("Nom, email et message sont obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        full_name: fullName,
        email,
        phone,
        subject,
        message,
      });
      toast.success("Message envoyé ! Réponse rapide garantie.");
      setFullName("");
      setEmail("");
      setPhone("");
      setSubject("");
      setMessage("");
    } catch (err) {
      toast.error("Erreur lors de l'envoi. Réessaie plus tard.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#050505] py-24 md:py-32 px-6 md:px-12 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9]"
            >
              UNE QUESTION ?
              <br />
              <span className="text-[#CCFF00]">PARLONS-EN.</span>
            </motion.h2>
            <p className="mt-6 text-zinc-400 leading-relaxed">
              Pour toute demande d&apos;information, devis personnalisé clubs
              ou collaboration, contacte-moi directement.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href="mailto:jerem1207@hotmail.com"
                data-testid="contact-email"
                className="flex items-center gap-4 group"
              >
                <span className="w-10 h-10 grid place-items-center bg-[#0a0a0a] border border-white/10 group-hover:border-[#CCFF00]/50">
                  <Mail className="w-4 h-4 text-[#CCFF00]" strokeWidth={1.5} />
                </span>
                <span>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Email</p>
                  <p className="text-white text-sm group-hover:text-[#CCFF00] transition-colors">
                    jerem1207@hotmail.com
                  </p>
                </span>
              </a>
              <a
                href="tel:+33667112294"
                data-testid="contact-phone"
                className="flex items-center gap-4 group"
              >
                <span className="w-10 h-10 grid place-items-center bg-[#0a0a0a] border border-white/10 group-hover:border-[#CCFF00]/50">
                  <Phone className="w-4 h-4 text-[#CCFF00]" strokeWidth={1.5} />
                </span>
                <span>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Téléphone</p>
                  <p className="text-white text-sm group-hover:text-[#CCFF00] transition-colors">
                    06 67 11 22 94
                  </p>
                </span>
              </a>
              <a
                href="https://www.instagram.com/jeremy_aymes/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram"
                className="flex items-center gap-4 group"
              >
                <span className="w-10 h-10 grid place-items-center bg-[#0a0a0a] border border-white/10 group-hover:border-[#CCFF00]/50">
                  <Instagram className="w-4 h-4 text-[#CCFF00]" strokeWidth={1.5} />
                </span>
                <span>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Instagram</p>
                  <p className="text-white text-sm group-hover:text-[#CCFF00] transition-colors">
                    @jeremy_aymes
                  </p>
                </span>
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              className="bg-[#0a0a0a] border border-white/10 p-8 md:p-10 rounded-sm grid grid-cols-1 md:grid-cols-2 gap-5"
              data-testid="contact-form"
            >
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Nom complet *</Label>
                <Input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Prénom Nom"
                  required
                  data-testid="contact-name-input"
                  className="mt-2 bg-black/50 border-white/10 text-white placeholder:text-zinc-600 h-12 rounded-sm focus-visible:ring-[#CCFF00]"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Email *</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ton@email.com"
                  required
                  data-testid="contact-email-input"
                  className="mt-2 bg-black/50 border-white/10 text-white placeholder:text-zinc-600 h-12 rounded-sm focus-visible:ring-[#CCFF00]"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Téléphone</Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="06 12 34 56 78"
                  data-testid="contact-phone-input"
                  className="mt-2 bg-black/50 border-white/10 text-white placeholder:text-zinc-600 h-12 rounded-sm focus-visible:ring-[#CCFF00]"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Sujet</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Devis club, infos forfait..."
                  data-testid="contact-subject-input"
                  className="mt-2 bg-black/50 border-white/10 text-white placeholder:text-zinc-600 h-12 rounded-sm focus-visible:ring-[#CCFF00]"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Message *</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Ton message..."
                  rows={5}
                  required
                  data-testid="contact-message-input"
                  className="mt-2 bg-black/50 border-white/10 text-white placeholder:text-zinc-600 rounded-sm focus-visible:ring-[#CCFF00]"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="md:col-span-2 mt-2 inline-flex items-center justify-center gap-3 bg-[#CCFF00] text-black font-bold uppercase tracking-wide text-sm px-7 py-4 rounded-sm hover:bg-white transition-colors disabled:opacity-50"
              >
                {submitting ? "Envoi…" : "Envoyer le message"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
